import React, { useState, useEffect } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  InvoiceDTO,
  InvoiceItemDTO,
  ClientDTO,
  CompanyInfoDTO,
  InvoiceTypeDTO,
  InvoiceStatusDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import AddNewButton from '../common/AddButton';
import Drawer from '../common/Drawer';
import InvoiceItemTable from '../tables/InvoiceItemTable';
import InvoiceItemForm from './InvoiceItemForm';
import { useClientApi } from '../../hooks/useClientApi';
import { useCompanyInfoApi } from '../../hooks/useCompanyInfoApi';
import { useInvoiceTypeApi } from '../../hooks/useInvoiceTypeApi';
import { useInvoiceStatusApi } from '../../hooks/useInvoiceStatusApi';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { showAlert } from '../../utils/sweetAlertUtils';
import { InvoiceTypeConstants } from '../../constants/invoiceConstants';

interface InvoiceFormProps {
  initialValues: InvoiceDTO;
  onSubmit: (data: InvoiceDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  creditNoteInfo: {
    id: 'credit-note-info',
    title: 'Credit Note Information',
  },
  invoiceDetails: {
    id: 'invoice-details',
    title: 'Invoice Details',
  },
  invoiceItems: {
    id: 'invoice-items',
    title: 'Invoice Items',
  },
  totals: {
    id: 'totals',
    title: 'Totals & VAT',
  },
} as const;

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<InvoiceDTO>(initialValues);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InvoiceItemDTO | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { search: searchClients, isLoading: isLoadingClients } = useClientApi();
  const { getById: getClientById } = useClientApi();
  const { search: searchCompanyInfo } = useCompanyInfoApi();
  const { search: searchInvoiceTypes, isLoading: isLoadingInvoiceTypes } = useInvoiceTypeApi();
  const { search: searchInvoiceStatuses, isLoading: isLoadingInvoiceStatuses } = useInvoiceStatusApi();
  const { search: searchInvoices, isLoading: isLoadingInvoices } = useInvoiceApi();
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceTypeDTO[]>([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState<InvoiceStatusDTO[]>([]);
  const [availableInvoices, setAvailableInvoices] = useState<InvoiceDTO[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoDTO | null>(null);
  const [isCreditNote, setIsCreditNote] = useState(false);

  const validationRules: ValidationRules = {
    clientId: [{ required: true, message: 'Client is required' }],
    invoiceTypeId: [{ required: true, message: 'Invoice Type is required' }],
    invoiceStatusId: [{ required: true, message: 'Invoice Status is required' }],
    creditsInvoiceId: [
      {
        custom: (value) => {
          if (isCreditNote && !value) {
            return false;
          }
          return true;
        },
        message: 'Original Invoice is required for Credit Notes',
      },
    ],
    total: [
      { required: true, message: 'Total is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Total must be a positive number',
      },
    ],
    dueDate: [
      { required: true, message: 'Due Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Due Date must be a valid date',
      },
    ],
  };

  const { errors, validate } = useFormValidation<InvoiceDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  // Watch for invoice type changes
  useEffect(() => {
    const selectedIsCreditNote = formData.invoiceTypeId === InvoiceTypeConstants.CreditNote;
    setIsCreditNote(selectedIsCreditNote);

    // Load available invoices when credit note is selected and client is chosen
    if (selectedIsCreditNote && formData.clientId) {
      loadClientInvoices(formData.clientId);
    }
  }, [formData.invoiceTypeId, formData.clientId]);

  const loadClientInvoices = async (clientId: string) => {
    try {
      const response = await searchInvoices({
        filters: {
          clientId: clientId,
          invoiceTypeId: InvoiceTypeConstants.Standard, // Only show standard invoices
        },
        page: 1,
        pageSize: 100,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setAvailableInvoices(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching client invoices:', error);
    }
  };

  const handleAddItem = async () => {
    try {
      if (formData.clientId) {
        const response = await getClientById(formData.clientId);
        const client = response?.data as ClientDTO;
        setEditingItem({
          id: undefined,
          description: '',
          quantity: 1,
          rate: client.defaultHourlyRate || 0,
          amount: client.defaultHourlyRate || 0,
        });
      } else {
        setEditingItem({
          id: undefined,
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
        });
      }
      setEditingIndex(null);
      setIsDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching client details:', error);
      setEditingItem({
        id: undefined,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      });
      setEditingIndex(null);
      setIsDrawerOpen(true);
    }
  };

  const handleEditItem = (item: InvoiceItemDTO) => {
    const index = formData.invoiceItems?.findIndex((i) => 
      i.id ? i.id === item.id : i === item
    ) ?? -1;
    
    setEditingItem(item);
    setEditingIndex(index);
    setIsDrawerOpen(true);
  };

  const handleDeleteItem = (item: InvoiceItemDTO) => {
    const updatedItems = formData.invoiceItems?.filter((i) => 
      i.id ? i.id !== item.id : i !== item
    ) || [];
    
    const subtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const vatAmount = formData.includeVat && formData.vatRate 
      ? subtotal * (formData.vatRate / 100) 
      : 0;
    const total = subtotal + vatAmount;

    setFormData((prev) => ({
      ...prev,
      invoiceItems: updatedItems,
      subtotal,
      vatAmount,
      total,
    }));
  };

  const handleItemSubmit = (item: InvoiceItemDTO) => {
    const updatedItems = [...(formData.invoiceItems || [])];
    
    if (editingIndex !== null && editingIndex >= 0) {
      updatedItems[editingIndex] = item;
    } else {
      updatedItems.push(item);
    }

    const subtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const vatAmount = formData.includeVat && formData.vatRate 
      ? subtotal * (formData.vatRate / 100) 
      : 0;
    const total = subtotal + vatAmount;

    setFormData((prev) => ({
      ...prev,
      invoiceItems: updatedItems,
      subtotal,
      vatAmount,
      total,
    }));
    setIsDrawerOpen(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  const handleIncludeVatChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const includeVat = (e.target as HTMLInputElement).checked;
    const subtotal = formData.subtotal || 0;
    const vatAmount = includeVat && formData.vatRate 
      ? subtotal * (formData.vatRate / 100) 
      : 0;
    const total = subtotal + vatAmount;

    setFormData((prev) => ({
      ...prev,
      includeVat,
      vatAmount,
      total,
    }));
  };

  const handleVatRateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const vatRate = parseFloat(e.target.value) || 0;
    const subtotal = formData.subtotal || 0;
    const vatAmount = formData.includeVat 
      ? subtotal * (vatRate / 100) 
      : 0;
    const total = subtotal + vatAmount;

    setFormData((prev) => ({
      ...prev,
      vatRate,
      vatAmount,
      total,
    }));
  };

  // Load company info and set default VAT for new invoices
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await searchCompanyInfo({
          page: 1,
          pageSize: 1,
          sortBy: 'Name',
          sortDescending: false,
        });
        const companyData = response?.data?.results?.[0] as CompanyInfoDTO;
        setCompanyInfo(companyData);

        if (!initialValues.id && companyData) {
          setFormData((prev) => ({
            ...prev,
            includeVat: true,
            vatRate: companyData.vatRate || 0,
          }));
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    loadCompanyInfo();
  }, [initialValues.id]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await searchClients({
          page: 1,
          pageSize: 100,
          sortBy: 'Name',
          sortDescending: false,
        });
        setClients(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    loadClients();
  }, []);

  useEffect(() => {
    const loadInvoiceTypes = async () => {
      try {
        const response = await searchInvoiceTypes({
          page: 1,
          pageSize: 100,
          sortBy: 'Name',
          sortDescending: false,
        });
        setInvoiceTypes(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching invoice types:', error);
      }
    };

    loadInvoiceTypes();
  }, []);

  useEffect(() => {
    const loadInvoiceStatuses = async () => {
      try {
        const response = await searchInvoiceStatuses({
          page: 1,
          pageSize: 100,
          sortBy: 'Name',
          sortDescending: false,
        });
        setInvoiceStatuses(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching invoice statuses:', error);
      }
    };

    loadInvoiceStatuses();
  }, []);

  const handleAddItemClick = async () => {
    if (!formData.clientId) {
      await showAlert({
        title: 'Client Required',
        text: 'Please select a client before adding invoice items',
        icon: 'warning'
      });
      return;
    }
    handleAddItem();
  };

  const sections = isCreditNote 
    ? [FORM_SECTIONS.basicInfo, FORM_SECTIONS.creditNoteInfo, FORM_SECTIONS.invoiceDetails, FORM_SECTIONS.invoiceItems, FORM_SECTIONS.totals]
    : [FORM_SECTIONS.basicInfo, FORM_SECTIONS.invoiceDetails, FORM_SECTIONS.invoiceItems, FORM_SECTIONS.totals];

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="clientId"
          label="Client"
          type="dropdown"
          value={formData.clientId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Client"
          required={true}
          error={errors.clientId}
          options={clients.map((client) => ({
            value: client.id,
            label: client.name,
          }))}
          isLoading={isLoadingClients}
        />
        <FormField
          id="invoiceTypeId"
          label="Invoice Type"
          type="dropdown"
          value={formData.invoiceTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Invoice Type"
          required={true}
          error={errors.invoiceTypeId}
          options={invoiceTypes.map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          isLoading={isLoadingInvoiceTypes}
        />
        <FormField
          id="invoiceStatusId"
          label="Invoice Status"
          type="dropdown"
          value={formData.invoiceStatusId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Invoice Status"
          required={true}
          error={errors.invoiceStatusId}
          options={invoiceStatuses.map((status) => ({
            value: status.id,
            label: status.name,
          }))}
          isLoading={isLoadingInvoiceStatuses}
        />
        <FormField
          id="invoiceNumber"
          label="Invoice Number"
          type="text"
          value={formData.invoiceNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Auto generated on save"
          required={false}
          error={errors.invoiceNumber}
          disabled
        />
      </FormSection>

      {isCreditNote && (
        <FormSection
          id={FORM_SECTIONS.creditNoteInfo.id}
          title={FORM_SECTIONS.creditNoteInfo.title}
        >
          <FormField
            id="creditsInvoiceId"
            label="Original Invoice"
            type="dropdown"
            value={formData.creditsInvoiceId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Invoice to Credit"
            required={true}
            error={errors.creditsInvoiceId}
            options={availableInvoices.map((invoice) => ({
              value: invoice.id,
              label: `${invoice.invoiceNumber} - R${invoice.total?.toFixed(2)} - ${new Date(invoice.createdUtc || '').toLocaleDateString()}`,
            }))}
            isLoading={isLoadingInvoices}
          />
          <FormField
            id="creditReason"
            label="Reason for Credit Note"
            type="textarea"
            value={formData.creditReason || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter reason for issuing this credit note"
            required={false}
          />
        </FormSection>
      )}

      <FormSection
        id={FORM_SECTIONS.invoiceDetails.id}
        title={FORM_SECTIONS.invoiceDetails.title}
      >
        <FormField
          id="dueDate"
          label="Due Date"
          type="date"
          value={formData.dueDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Due Date"
          required={true}
          error={errors.dueDate}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.invoiceItems.id}
        title={FORM_SECTIONS.invoiceItems.title}
        headerActions={
          <AddNewButton
            variant="secondary"
            label="Add Item"
            onClick={handleAddItemClick}
          />
        }
      >
        <InvoiceItemTable
          data={formData.invoiceItems || []}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          isLoading={false}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.totals.id}
        title={FORM_SECTIONS.totals.title}
      >
        <FormField
          id="subtotal"
          label="Subtotal (excl. VAT)"
          type="number"
          value={formData.subtotal || '0'}
          onChange={(e) => handleChange(e, setFormData)}
          disabled
        />
        <FormField
          id="includeVat"
          label="Include VAT"
          type="checkbox"
          value={formData.includeVat || false}
          onChange={handleIncludeVatChange}
        />
        {formData.includeVat && (
          <>
            <FormField
              id="vatRate"
              label="VAT Rate (%)"
              type="number"
              value={formData.vatRate || '0'}
              onChange={handleVatRateChange}
              placeholder="Enter VAT Rate"
            />
            <FormField
              id="vatAmount"
              label={`VAT Amount (${formData.vatRate || 0}%)`}
              type="number"
              value={formData.vatAmount || '0'}
              onChange={(e) => handleChange(e, setFormData)}
              disabled
            />
          </>
        )}
        <FormField
          id="total"
          label={formData.includeVat ? "Total (incl. VAT)" : "Total"}
          type="number"
          value={formData.total || '0'}
          onChange={(e) => handleChange(e, setFormData)}
          disabled
        />
      </FormSection>

      {isDrawerOpen && editingItem && (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setEditingItem(null);
            setEditingIndex(null);
          }}
          title={editingIndex !== null ? 'Edit Invoice Item' : 'Add Invoice Item'}
        >
          <InvoiceItemForm
            initialValues={editingItem}
            onSubmit={handleItemSubmit}
            onCancel={() => {
              setIsDrawerOpen(false);
              setEditingItem(null);
              setEditingIndex(null);
            }}
          />
        </Drawer>
      )}
    </Form>
  );
};

export default InvoiceForm;
